/**
 * Agent Feedback & Case Export — boundary contract schemas.
 *
 * Owned here, not beside the routes: `apps/api/src/routes/manager/agent-feedback.routes.ts` and
 * `apps/api/src/routes/admin/admin-agent-feedback.routes.ts` register these schemas, and the
 * landlord and admin frontends derive their request/response types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers (agent-observability BR-20..BR-33):
 *   GET      /landlord/api/cases/:id/agent-feedback
 *   POST     /landlord/api/agent-feedback/:feedbackId/withdraw
 *   Admin    /admin/api/agent-feedback (list, open-count, detail, create-on-case, transition,
 *            request export, download)
 *
 * A landlord never creates or edits a report: items arrive only from an improvement conversation
 * (OBS-BR-47), so the landlord surface is list + withdraw. `FeedbackInputSchema` remains for the
 * admin create-on-case route.
 *
 * The views mirror the service types in apps/api/src/services/agent/agent-feedback.service.ts
 * field for field. Validation of the free-text/anchor rules (BR-20/21/22) is the service's job;
 * these request schemas enforce shape and generous ceilings only, so the service's typed
 * FEEDBACK_* codes reach the caller.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const FeedbackBehaviorKindSchema = z.enum([
    'omission',
    'commission',
    'sequencing',
    'content',
    'tone',
    'escalation',
    'ignored_playbook',
    'other',
]);
export const FeedbackAnchorKindSchema = z.enum(['point', 'window', 'case']);
export const FeedbackStatusSchema = z.enum(['open', 'triaged', 'exported', 'resolved', 'dismissed', 'withdrawn']);
export const FeedbackAuthorKindSchema = z.enum(['landlord', 'admin']);
export const CaseExportStatusSchema = z.enum(['requested', 'ready', 'failed']);
// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------
export const FeedbackRefSchema = z
    .object({
    kind: z.enum(['run', 'case_event', 'draft']),
    ref: z.uuid(),
})
    .describe('A reference to a run, case event or draft on the case');
/** A capability id OR free text — exactly one (enforced by the service). */
export const FeedbackActionSchema = z
    .object({
    capability: z.string().max(100).optional(),
    text: z.string().max(5000).optional(),
})
    .describe('What the agent did (x) or should have done (y): a capability id or free text');
export const FeedbackInputSchema = z
    .object({
    behaviorKind: FeedbackBehaviorKindSchema,
    actionX: FeedbackActionSchema,
    actionY: FeedbackActionSchema.optional().describe('Required for sequencing, forbidden otherwise'),
    anchorKind: FeedbackAnchorKindSchema,
    anchorFrom: FeedbackRefSchema.optional(),
    anchorTo: FeedbackRefSchema.nullable().optional().describe('Window only; null/absent = case end'),
    evidence: z.array(FeedbackRefSchema).max(200).optional(),
    expectedBehavior: z.string().max(5000),
    comment: z.string().max(5000).optional(),
})
    .describe('Admin-authored feedback report body');
export const FeedbackIdParamsSchema = z.object({ feedbackId: z.uuid() });
// ---------------------------------------------------------------------------
// Landlord views
// ---------------------------------------------------------------------------
export const LandlordFeedbackViewSchema = z
    .object({
    id: z.uuid(),
    behaviorKind: FeedbackBehaviorKindSchema,
    actionX: FeedbackActionSchema,
    actionY: FeedbackActionSchema.nullable(),
    anchorKind: FeedbackAnchorKindSchema,
    anchorFrom: FeedbackRefSchema.nullable(),
    anchorTo: FeedbackRefSchema.nullable(),
    evidence: z.array(FeedbackRefSchema),
    expectedBehavior: z.string(),
    comment: z.string().nullable(),
    createdAt: z.string().describe('ISO 8601'),
    editable: z.literal(false).describe('A landlord can no longer edit an item (BR-24)'),
    withdrawable: z.boolean().describe('True only while the item is open (the landlord may withdraw it)'),
    fromConversation: z.boolean().describe('The item was created by an improvement conversation (OBS-BR-47)'),
    conversationId: z
        .uuid()
        .nullable()
        .describe('The creating improvement conversation, so the reports list can reopen it (IMP-BR-6); null otherwise'),
})
    .describe("A landlord's own feedback report — no status, resolution or export fields (BR-24)");
export const LandlordFeedbackListResponseSchema = z.object({
    eligible: z.boolean().describe('The case has had at least one processed agent run'),
    reports: z.array(LandlordFeedbackViewSchema),
});
// ---------------------------------------------------------------------------
// Admin views
// ---------------------------------------------------------------------------
export const CaseExportViewSchema = z
    .object({
    exportId: z.uuid(),
    status: CaseExportStatusSchema,
    failureReason: z.string().nullable(),
    scrubReplacedCount: z.number().int().nullable(),
    residualWarnings: z.array(z.string()).nullable(),
    parts: z.record(z.string(), z.string()).nullable(),
    requestedAt: z.string().describe('ISO 8601'),
    settledAt: z.string().nullable().describe('ISO 8601'),
})
    .describe('One case-export request and its settled outcome');
export const AdminFeedbackViewSchema = z
    .object({
    id: z.uuid(),
    caseId: z.uuid(),
    agentKindId: z.uuid(),
    landlordScopeId: z.string(),
    behaviorKind: FeedbackBehaviorKindSchema,
    actionX: FeedbackActionSchema,
    actionY: FeedbackActionSchema.nullable(),
    anchorKind: FeedbackAnchorKindSchema,
    anchorFrom: FeedbackRefSchema.nullable(),
    anchorTo: FeedbackRefSchema.nullable(),
    evidence: z.array(FeedbackRefSchema),
    expectedBehavior: z.string(),
    comment: z.string().nullable(),
    authorKind: FeedbackAuthorKindSchema,
    authorUserId: z.string(),
    conversationId: z.uuid().nullable().describe('The improvement conversation that created the item, if any (OBS-BR-49)'),
    status: FeedbackStatusSchema,
    resolutionNote: z.string().nullable(),
    dismissReason: z.string().nullable(),
    lastExportedAt: z.string().nullable().describe('ISO 8601'),
    createdAt: z.string().describe('ISO 8601'),
    updatedAt: z.string().describe('ISO 8601'),
    exports: z.array(CaseExportViewSchema),
})
    .describe('Admin view of a feedback item, including triage state and its exports');
export const AdminFeedbackListQuerySchema = z.object({
    status: FeedbackStatusSchema.optional(),
    behaviorKind: FeedbackBehaviorKindSchema.optional(),
    agentKindSlug: z.string().min(1).optional(),
    landlordScopeId: z.string().min(1).optional(),
    caseId: z.uuid().optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const AdminFeedbackListResponseSchema = z.object({
    items: z.array(AdminFeedbackViewSchema),
    nextCursor: z.string().nullable(),
});
export const AdminFeedbackOpenCountResponseSchema = z.object({ count: z.number().int().nonnegative() });
export const FeedbackTransitionBodySchema = z
    .union([
    z.object({ to: z.enum(['triaged', 'dismissed', 'resolved']), note: z.string().max(5000).optional() }),
    z.object({ reopen: z.literal(true) }),
])
    .describe('Feedback-status transition (BR-26): a target status with optional note, or a reopen');
//# sourceMappingURL=feedback.js.map